import React, { useState, useEffect, useRef, Fragment } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useSelector } from "react-redux"

import {
  OrdersTableHeader,
  InfiniteScrollLoading,
  OrderTableRow,
  OrdersEmptyList,
  PageLoader
} from "components"
import {
  filterDuplicatedOrders,
  isOrderForThisUser,
  socketConnection
} from "utils"
import { getOrders } from "services"
import NcoFooter from "./NcoFooter"
import NcoHeader from "./NcoHeader"
import classes from "./index.module.scss"

const limit = 30

const NotConfirmedOrders = () => {
  const [skip, setSkip] = useState(0)
  const [orderCode, setOrderCode] = useState("")
  const [orders, setOrders] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [selectedOrders, setSelectedOrders] = useState({})

  const unmount = useRef(false)

  const branchId =
    useSelector(state => state.userAccountInfo.branchId)

  const onNotConfirmedOrderChanges = (jsonMessage) => {
    const message = JSON.parse(jsonMessage)

    const event = message.key

    const order = message.value.order

    if (!isOrderForThisUser(order)) return

    if (event === "orderCreated") {
      if (order.orderStatus !== "notConfirmed") return

      setOrders(prev => {
        const previousOrders = prev || []

        return [order, ...previousOrders]
      })
    }

    if (event === "orderUpdated") {
      setOrders(prev => {
        if (prev === null) return prev

        const exists =
          prev.some(o => o._id === order._id)

        if (!exists && order.orderStatus !== "notConfirmed") return prev

        return exists
          ? prev.map(o => o._id === order._id ? order : o)
          : [order, ...prev]
      })
    }

    if (event === "orderConfirmed") {
      setOrders(prev => {
        const exists =
          prev.some(o => o._id === order._id)

        if (!exists && order.orderStatus !== "notConfirmed") return prev


        return exists
          ? prev.map(o => o._id === order._id ? order : o)
          : [order, ...prev]
      })
    }
  }

  const handleSelectAll = (checked) => {
    if (!checked) {
      return setSelectedOrders({})
    }

    const selectedOrders = {}

    orders.forEach((o) => {
      selectedOrders[o._id] = o
    })

    setSelectedOrders(selectedOrders)
  }

  const handleLoadMore = async () => {
    try {
      const response =
        await getOrders({
          skip,
          limit,
          sortBy: "newstOrder",
          code: orderCode,
          branchId,
          statusMultiple: ["notConfirmed"]
        })

      if (unmount.current) return

      response.length !== limit
        ? setHasMore(false)
        : setSkip(prev => prev += limit)

      const filteredOrders =
        filterDuplicatedOrders(
          orders,
          response
        )

      setOrders(prev => [...prev, ...filteredOrders])
    } catch (err) {
      console.log(err)
    }
  }

  const GetOrders = async () => {
    if (orders !== null) {
      setSkip(0)

      setSelectedOrders({})

      setHasMore(true)

      setOrders(null)
    }

    try {
      const response =
        await getOrders({
          skip: 0,
          limit,
          sortBy: "newstOrder",
          code: orderCode,
          branchId,
          statusMultiple: ["notConfirmed"]
        })

      if (unmount.current) return

      response.length !== limit
        ? setHasMore(false)
        : setSkip(prev => prev += limit)

      setOrders(response)
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    GetOrders()
  }, [orderCode])

  useEffect(() => {
    const socket = socketConnection.getSocket()

    socket.on("basicEmit", onNotConfirmedOrderChanges)

    unmount.current = false

    return () => {
      unmount.current = true

      socket.removeListener("basicEmit", onNotConfirmedOrderChanges)
    }
  }, [])

  if (orders === null) return <PageLoader />

  const noOrders =
    !hasMore && orders.length === 0

  const allSelected =
    orders.length !== 0 &&
    Object.keys(selectedOrders).length === orders.length

  return (
    <Fragment>
      <NcoHeader
        setOrderCode={setOrderCode}
      />

      <div
        id="scrollable-div"
        className={classes["table-container"]}
      >
        <InfiniteScroll
          hasMore={hasMore}
          dataLength={skip}
          next={handleLoadMore}
          style={{ overflow: "unset" }}
          scrollableTarget="scrollable-div"
          loader={<InfiniteScrollLoading />}
          scrollThreshold="100px"
        >
          <table className={classes["table"]}>
            <OrdersTableHeader
              hideCheckBox
              onSelectAll={handleSelectAll}
              allSelected={allSelected}
            />
            {!noOrders
              ?
              <tbody>
                {orders.map(order =>
                  <OrderTableRow
                    key={order._id}
                    order={order}
                    setOrders={setOrders}
                    isSelected={!!selectedOrders[order._id]}
                    setSelectedOrders={setSelectedOrders}
                    showCheckbox={order.orderStatus === "canceled"}
                  />
                )}
              </tbody>
              :
              <OrdersEmptyList />
            }
          </table>
        </InfiniteScroll>
      </div>

      <NcoFooter
        setOrders={setOrders}
        selectedOrders={selectedOrders}
        setSelectedOrders={setSelectedOrders}
      />
    </Fragment>
  )
}

export default NotConfirmedOrders